<template>
  <section class="send-settings-panel card">
    <div class="card-body">
      <div class="send-settings-panel__head">
        <h2 class="h6 mb-0">Sending settings</h2>
        <p class="text-muted small mb-0">Applies to this workspace's marketing sends</p>
      </div>

      <div v-if="loading" class="text-muted small mt-3">Loading…</div>
      <div v-else-if="error" class="alert alert-danger py-2 small mt-3">{{ error }}</div>

      <template v-else>
        <!-- Scope banner — is this the platform-defaults layer or a workspace? -->
        <div :class="['ssp-scope', scope.is_platform_default_layer ? 'is-platform' : 'is-workspace']">
          <template v-if="scope.is_platform_default_layer">
            <strong>Platform defaults.</strong> These apply to every workspace that hasn't set its own.
          </template>
          <template v-else>
            <strong>Workspace settings.</strong> Any field you leave unset falls back to the platform default.
          </template>
        </div>

      <form class="send-settings-panel__form mt-3" @submit.prevent="save">
        <!-- Sender rotation -->
        <div class="ssp-field">
          <div class="form-check form-switch">
            <input id="ssp-rotation" v-model="form.sender_rotation" class="form-check-input" type="checkbox">
            <label class="form-check-label" for="ssp-rotation">Sender rotation</label>
            <span :class="['ssp-badge', sourceBadge('sender_rotation').cls]">{{ sourceBadge('sender_rotation').text }}</span>
          </div>
          <p class="ssp-help">Rotate verified sender identities per send instead of always using the default sender.</p>
        </div>

        <!-- Send window -->
        <div class="ssp-field">
          <div class="form-check form-switch">
            <input id="ssp-window" v-model="form.send_window_enabled" class="form-check-input" type="checkbox">
            <label class="form-check-label" for="ssp-window">Send window</label>
            <span :class="['ssp-badge', sourceBadge('send_window_enabled').cls]">{{ sourceBadge('send_window_enabled').text }}</span>
          </div>
          <p class="ssp-help">Restrict marketing sends to specific days and hours in a chosen timezone.</p>

          <div v-if="form.send_window_enabled" class="ssp-window">
            <div class="ssp-days">
              <button
                v-for="d in weekdays"
                :key="d.value"
                type="button"
                class="ssp-day-chip"
                :class="{ 'is-active': form.send_window_days.includes(d.value) }"
                @click="toggleDay(d.value)"
              >{{ d.label }}</button>
            </div>

            <div class="row g-2 mt-2 align-items-end">
              <div class="col-auto">
                <label class="form-label small text-muted" for="ssp-start">Start</label>
                <select id="ssp-start" v-model.number="form.send_window_start_hour" class="form-select form-select-sm">
                  <option v-for="h in 24" :key="'start-' + h" :value="h - 1">{{ fmtHour(h - 1) }}</option>
                </select>
              </div>
              <div class="col-auto">
                <label class="form-label small text-muted" for="ssp-end">End</label>
                <select id="ssp-end" v-model.number="form.send_window_end_hour" class="form-select form-select-sm">
                  <option v-for="h in 24" :key="'end-' + h" :value="h">{{ fmtHour(h) }}</option>
                </select>
              </div>
              <div class="col">
                <label class="form-label small text-muted" for="ssp-tz">Timezone</label>
                <input id="ssp-tz" v-model="form.send_window_timezone" type="text" class="form-control form-control-sm" placeholder="Europe/London">
              </div>
            </div>
            <p v-if="windowError" class="ssp-error">{{ windowError }}</p>
          </div>
        </div>

        <!-- Send-time optimisation default (W2) -->
        <div class="ssp-field">
          <div class="ssp-label">
            Default send-time mode
            <span :class="['ssp-badge', sourceBadge('send_timing_default').cls]">{{ sourceBadge('send_timing_default').text }}</span>
            <span v-if="!stoEntitled" class="ssp-lock">Available on Growth</span>
          </div>
          <p class="ssp-help">The default applied to new campaigns. <strong>Optimise</strong> sends each contact at their learned best time, always inside the send window.</p>
          <div class="form-check">
            <input id="ssp-timing-window" v-model="form.send_timing_default" class="form-check-input" type="radio" value="window">
            <label class="form-check-label" for="ssp-timing-window">Send window only</label>
          </div>
          <div class="form-check">
            <input id="ssp-timing-optimise" v-model="form.send_timing_default" class="form-check-input" type="radio" value="optimise" :disabled="!stoEntitled">
            <label class="form-check-label" for="ssp-timing-optimise">Optimise send time per contact</label>
          </div>
        </div>

        <!-- Spintax -->
        <div class="ssp-field">
          <div class="ssp-label">Spintax</div>
          <p class="ssp-help">
            Spintax is always active — write <code>{{ spintaxExample }}</code> in subjects or bodies to send randomised variants.
          </p>
        </div>

        <div class="ssp-actions">
          <button type="submit" class="btn btn-primary btn-sm" :disabled="busy.save || !isDirty">
            <CygnusIcon name="spinner" :spin="true" v-if="busy.save" />
            <span v-else>Save</span>
          </button>
          <span v-if="message" :class="['ssp-msg', ok ? 'is-ok' : 'is-error']">{{ message }}</span>
        </div>
      </form>
      </template>
    </div>
  </section>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

const ENDPOINT = '/api/v1/admin/email/send-settings';

export default {
  name: 'SendSettingsPanel',
  data() {
    return {
      api: useApi(),
      loading: true,
      error: null,
      busy: { save: false },
      message: '',
      ok: false,
      windowError: '',
      spintaxExample: '{option A|option B}',
      weekdays: [
        { value: 1, label: 'Mon' },
        { value: 2, label: 'Tue' },
        { value: 3, label: 'Wed' },
        { value: 4, label: 'Thu' },
        { value: 5, label: 'Fri' },
        { value: 6, label: 'Sat' },
        { value: 7, label: 'Sun' },
      ],
      form: {
        sender_rotation: false,
        send_window_enabled: false,
        send_window_days: [],
        send_window_start_hour: 9,
        send_window_end_hour: 17,
        send_window_timezone: '',
        // W2 — default send-time mode for new campaigns.
        send_timing_default: 'window',
      },
      // Whether this tenant may pick 'optimise' (send_time_optimisation flag).
      stoEntitled: false,
      // Per-field provenance ('workspace' | 'platform' | 'default') + which
      // layer we're editing — surfaced as badges + the scope banner.
      sources: {},
      scope: { tenant_id: null, is_platform_default_layer: false },
      // Snapshot of the last-loaded/saved form, used to diff on save.
      pristine: null,
    };
  },
  computed: {
    isDirty() {
      return Object.keys(this.diffBody()).length > 0;
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    fmtHour(h) { return String(h).padStart(2, '0') + ':00'; },
    // Map a field's source layer to a badge. On the platform-defaults layer a
    // stored value IS the platform default, so 'workspace' collapses to the
    // platform label there (the operator is editing the shared defaults).
    sourceBadge(field) {
      const src = this.sources?.[field] || 'default';
      if (this.scope.is_platform_default_layer) {
        return src === 'default'
          ? { text: 'Default', cls: 'is-default' }
          : { text: 'Platform default', cls: 'is-platform' };
      }
      if (src === 'workspace') return { text: 'Workspace override', cls: 'is-workspace' };
      if (src === 'platform') return { text: 'Platform default', cls: 'is-platform' };
      return { text: 'Default', cls: 'is-default' };
    },
    toggleDay(v) {
      const days = this.form.send_window_days;
      const idx = days.indexOf(v);
      if (idx === -1) days.push(v);
      else days.splice(idx, 1);
      days.sort((a, b) => a - b);
    },
    applySettings(payload) {
      const settings = payload?.settings || {};
      const sw = settings?.send_window || {};
      const st = settings?.send_timing || {};
      this.stoEntitled = !!st.entitled;
      this.sources = payload?.sources || {};
      this.scope = payload?.scope || { tenant_id: null, is_platform_default_layer: false };
      this.form = {
        sender_rotation: !!settings?.sender_rotation?.enabled,
        send_window_enabled: !!sw.enabled,
        send_window_days: Array.isArray(sw.days) ? [...sw.days].sort((a, b) => a - b) : [],
        send_window_start_hour: Number.isInteger(sw.start_hour) ? sw.start_hour : 9,
        send_window_end_hour: Number.isInteger(sw.end_hour) ? sw.end_hour : 17,
        send_window_timezone: sw.timezone || '',
        send_timing_default: st.default === 'optimise' ? 'optimise' : 'window',
      };
      this.pristine = JSON.parse(JSON.stringify(this.form));
    },
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const res = await this.api.get(ENDPOINT);
        const payload = res?.data ?? res;
        if (payload?.success === false) {
          this.error = payload?.error || 'Failed to load send settings.';
        } else {
          this.applySettings(payload);
        }
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Failed to load send settings.';
      }
      this.loading = false;
    },
    // Builds a PUT body containing only fields that differ from the
    // last-loaded/saved snapshot. Empty day list / blank timezone are
    // sent as null so the server restores its default for that field.
    diffBody() {
      const body = {};
      const p = this.pristine || {};
      if (this.form.sender_rotation !== p.sender_rotation) {
        body.sender_rotation = this.form.sender_rotation;
      }
      if (this.form.send_window_enabled !== p.send_window_enabled) {
        body.send_window_enabled = this.form.send_window_enabled;
      }
      const daysNow = JSON.stringify(this.form.send_window_days);
      const daysWas = JSON.stringify(p.send_window_days || []);
      if (daysNow !== daysWas) {
        body.send_window_days = this.form.send_window_days.length ? this.form.send_window_days : null;
      }
      if (this.form.send_window_start_hour !== p.send_window_start_hour) {
        body.send_window_start_hour = this.form.send_window_start_hour;
      }
      if (this.form.send_window_end_hour !== p.send_window_end_hour) {
        body.send_window_end_hour = this.form.send_window_end_hour;
      }
      const tzNow = (this.form.send_window_timezone || '').trim();
      const tzWas = (p.send_window_timezone || '').trim();
      if (tzNow !== tzWas) {
        body.send_window_timezone = tzNow || null;
      }
      if (this.form.send_timing_default !== p.send_timing_default) {
        body.send_timing_default = this.form.send_timing_default;
      }
      return body;
    },
    async save() {
      this.windowError = '';
      this.message = '';
      if (this.form.send_window_enabled && this.form.send_window_end_hour <= this.form.send_window_start_hour) {
        this.windowError = 'End hour must be after start hour.';
        return;
      }
      const body = this.diffBody();
      if (Object.keys(body).length === 0) {
        this.ok = true;
        this.message = 'No changes to save.';
        return;
      }
      this.busy.save = true;
      try {
        const res = await this.api.put(ENDPOINT, body);
        const payload = res?.data ?? res;
        if (payload?.success === false) {
          this.ok = false;
          this.message = payload?.error || 'Save failed.';
        } else {
          this.applySettings(payload);
          this.ok = true;
          this.message = 'Saved.';
        }
      } catch (e) {
        this.ok = false;
        this.message = e?.response?.data?.error || e?.message || 'Save failed.';
      }
      this.busy.save = false;
    },
  },
};
</script>

<style>
.send-settings-panel { background: var(--m-bg-card, #FFF); margin-top: 24px; }
.send-settings-panel__form { display: flex; flex-direction: column; gap: 18px; }

.ssp-field + .ssp-field { border-top: 1px solid var(--m-border, #E2E8F0); padding-top: 16px; }
.ssp-label { font-size: 0.9rem; font-weight: 500; margin-bottom: 4px; }
.ssp-lock {
  margin-left: 8px; font-size: 0.66rem; font-weight: 700; letter-spacing: 0.02em; text-transform: uppercase;
  color: var(--m-violet, #7C3AED); background: var(--m-violet-soft, #F3EEFF);
  border: 1px solid var(--m-violet-border, #E4D7FB); border-radius: 100px; padding: 1px 9px; vertical-align: middle;
}
.ssp-help { font-size: 0.8rem; color: var(--m-text-secondary, #475569); margin: 4px 0 0; }
.ssp-help code { background: #F1F5F9; padding: 2px 6px; border-radius: 3px; font-size: 0.78rem; }

/* Scope banner — workspace vs platform-defaults layer. */
.ssp-scope {
  margin-top: 12px; padding: 8px 12px; border-radius: 8px;
  font-size: 0.8rem; line-height: 1.4;
  border: 1px solid var(--m-border, #E2E8F0);
  color: var(--m-text-secondary, #475569);
  background: var(--m-bg-subtle, #F8FAFC);
}
.ssp-scope.is-platform {
  color: var(--m-violet, #7C3AED);
  background: var(--m-violet-soft, #F3EEFF);
  border-color: var(--m-violet-border, #E4D7FB);
}

/* Per-field provenance badge. */
.ssp-badge {
  margin-left: 8px; font-size: 0.62rem; font-weight: 700; letter-spacing: 0.03em;
  text-transform: uppercase; border-radius: 100px; padding: 1px 8px; vertical-align: middle;
  border: 1px solid transparent;
}
.ssp-badge.is-workspace {
  color: var(--m-primary, #2563EB); background: var(--m-primary-soft, #EFF4FF);
  border-color: var(--m-primary-border, #D3E0FB);
}
.ssp-badge.is-platform {
  color: var(--m-text-secondary, #475569); background: var(--m-bg-subtle, #F1F5F9);
  border-color: var(--m-border, #E2E8F0);
}
.ssp-badge.is-default {
  color: var(--m-text-muted, #94A3B8); background: transparent;
  border-color: var(--m-border, #E2E8F0);
}

.ssp-window { margin-top: 12px; }
.ssp-days { display: flex; flex-wrap: wrap; gap: 6px; }
.ssp-day-chip {
  border: 1px solid var(--m-border, #E2E8F0);
  background: var(--m-bg-card, #FFF);
  color: var(--m-text-secondary, #475569);
  border-radius: 100px;
  padding: 3px 12px;
  font-size: 0.78rem;
  line-height: 1.4;
  cursor: pointer;
}
.ssp-day-chip.is-active {
  background: var(--m-primary, #2563EB);
  border-color: var(--m-primary, #2563EB);
  color: #FFF;
}
.ssp-error { color: var(--m-status-danger, #EF4444); font-size: 0.8rem; margin: 8px 0 0; }

.ssp-actions { display: flex; align-items: center; gap: 10px; }
.ssp-msg { font-size: 0.85rem; }
.ssp-msg.is-ok { color: var(--m-status-success, #10B981); }
.ssp-msg.is-error { color: var(--m-status-danger, #EF4444); }
</style>
